/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	submitGetMissedContributions,
	submitCheckAutoDraftEligibility,
	fetchAutoDraftUsersAsAdmin,
	fetchAutoDraftUserByIdAsAdmin,
	fetchAutoDraftSummaryAsAdmin,
	cancelAutoDraftUserAsAdmin,
	fetchAutoDraftResultsAsAdmin,
	fetchAutoDraftResultsByDateAsAdmin,
	fetchAutoDraftFailedAsAdmin,
	fetchAutoDraftRetriesAsAdmin,
	fetchAutoDraftAttemptByIdAsAdmin,
	AutoDraftResultsParams,
	AutoDraftResultsByDateParams,
	AutoDraftFailedParams,
	AutoDraftRetryParams,
} from "../services/autoDraftService";

export interface MissedContribution {
	unionId: string;
	circleName: string;
	circleImage?: string;
	contributionAmount: number;
	missedCount: number;
	totalDueCents: number;
	totalDueDollars: number;
	contributionDays: string[];
	isContributionDayToday: boolean;
	isTodayAlreadyPaid?: boolean; // True if today is a contribution day and has already been paid
	cycleStartDate: string;
	cycleEndDate: string;
	hasAutoPayEnabled: boolean;
	unpaidDates?: string[]; // Array of YYYY-MM-DD dates that were missed
}

// ==================== Admin Auto-Draft Types ====================

export interface CircleAutoDraftInfo {
	circleId: string;
	circleName: string;
	circleImage: string;
	isLeader: boolean;
	contributionAmount: number;
	contributionDays: string[];
	hasActiveCycle: boolean;
	activeCycleId?: string;
	activeCycleEndDate?: Date;
	memberJoinedAt?: Date;
}

export interface AutoDraftUserInfo {
	userId: string;
	firstName: string;
	lastName: string;
	profileImage?: string;
	email: string;
	phone?: string;
	timezone: string;
	autoPayEnabled: boolean;
	hasDefaultPaymentMethod: boolean;
	cardLast4Digits: string;
	defaultPaymentMethodType?: string;
	circles: CircleAutoDraftInfo[];
}

export interface AutoDraftSummaryData {
	statistics: {
		totalUsersWithAutoDraft: number;
		usersWithValidPaymentMethod: number;
		usersWithoutPaymentMethod: number;
		usersInActiveCircles: number;
		totalCircleMemberships: number;
		activeCycleMemberships: number;
	};
	usersReadyForAutoDraft: {
		userId: string;
		name: string;
		email: string;
		profileImage?: string;
		activeCircleCount: number;
		activeCircles: {
			circleName: string;
			circleImage: string;
			contributionAmount: number;
		}[];
	}[];
	usersWithoutPaymentMethod: {
		userId: string;
		name: string;
		email: string;
		profileImage?: string;
	}[];
}

/**
 * Get all missed contributions for the authenticated user across all their unions
 */
export const getMissedContributions = createAsyncThunk(
	"autoDraft/getMissedContributions",
	async (_, { rejectWithValue }) => {
		try {
			const response = await submitGetMissedContributions();
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message ||
					error.message ||
					"Failed to retrieve missed contributions",
			);
		}
	},
);

/**
 * Check auto-draft eligibility for a specific union
 */
export const checkAutoDraftEligibility = createAsyncThunk(
	"autoDraft/checkEligibility",
	async (unionId: string, { rejectWithValue }) => {
		try {
			const response = await submitCheckAutoDraftEligibility(unionId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message ||
					error.message ||
					"Failed to check auto-draft eligibility",
			);
		}
	},
);

// ==================== Admin Auto-Draft Actions ====================

/**
 * Get all users with auto-draft enabled (admin only)
 */
export const getAutoDraftUsersAsAdmin = createAsyncThunk(
	"admin/getAutoDraftUsers",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftUsersAsAdmin();
			// API returns { success, data, meta } - extract data array
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft users";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get auto-draft details for a specific user (admin only)
 */
export const getAutoDraftUserByIdAsAdmin = createAsyncThunk(
	"admin/getAutoDraftUserById",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftUserByIdAsAdmin(userId);
			// API returns { success, data, meta } - extract data object
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft user details";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get auto-draft summary statistics (admin only)
 */
export const getAutoDraftSummaryAsAdmin = createAsyncThunk(
	"admin/getAutoDraftSummary",
	async (_, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftSummaryAsAdmin();
			// API returns { success, data, meta } - extract data object
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft summary";
			return rejectWithValue(message);
		}
	},
);

/**
 * Cancel/disable auto-draft for a specific user (admin only)
 */
export const cancelAutoDraftAsAdmin = createAsyncThunk(
	"admin/cancelAutoDraft",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await cancelAutoDraftUserAsAdmin(userId);
			// API returns { success, data, meta } - extract data object
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to cancel auto-draft";
			return rejectWithValue(message);
		}
	},
);

// ==================== Admin Auto-Draft Results Types ====================

export interface AutoDraftAttemptMember {
	userId: string;
	name: string;
	email: string;
}

export interface AutoDraftAttemptCircle {
	circleId: string;
	name: string;
}

export interface AutoDraftAttemptAmount {
	cents: number;
	formatted: string;
}

export interface AutoDraftAttemptBase {
	attemptId: string;
	member: AutoDraftAttemptMember | null;
	circle: AutoDraftAttemptCircle | null;
	amount: AutoDraftAttemptAmount;
	status: string;
	attemptedAt: string;
	retryCount: number;
	maxRetries: number;
}

export interface AutoDraftAttemptWithFailure extends AutoDraftAttemptBase {
	failureReason?: string;
	failureCode?: string;
	nextRetryAt?: string;
}

export interface AutoDraftAttemptDetailed extends AutoDraftAttemptWithFailure {
	dueWindowIds: string[];
	currency: string;
	processorReference: {
		paymentIntentId?: string;
		paymentMethodId?: string;
		chargeId?: string;
		balanceTxnId?: string;
	};
	idempotencyKey: string;
	createdAt: string;
	updatedAt: string;
}

export interface AutoDraftResultSummary {
	total: number;
	successful: number;
	failed: number;
	retrying: number;
	pending: number;
	totalAmountSuccessful: number;
	totalAmountFailed: number;
}

export interface AutoDraftResultForDateResponse {
	date: string;
	timezone: string;
	summary: AutoDraftResultSummary;
	attempts: {
		successful: AutoDraftAttemptBase[];
		failed: AutoDraftAttemptWithFailure[];
		retrying: AutoDraftAttemptWithFailure[];
		pending: AutoDraftAttemptBase[];
	};
}

export interface AutoDraftPagination {
	total: number;
	page: number;
	limit: number;
	pages: number;
}

export interface AutoDraftFailureAnalysis {
	byReason: Record<string, number>;
	byCode: Record<string, number>;
}

export interface AutoDraftFailedAttemptsResponse {
	attempts: AutoDraftAttemptWithFailure[];
	pagination: AutoDraftPagination;
	failureAnalysis: AutoDraftFailureAnalysis;
}

export interface AutoDraftRetryAttemptsResponse {
	attempts: AutoDraftAttemptWithFailure[];
	pagination: AutoDraftPagination;
	upcomingRetries: AutoDraftAttemptWithFailure[];
}

// ==================== Admin Auto-Draft Results Actions ====================

/**
 * Get today's auto-draft results (admin only)
 */
export const getAutoDraftResultsAsAdmin = createAsyncThunk(
	"admin/getAutoDraftResults",
	async (params: AutoDraftResultsParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftResultsAsAdmin(params);
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft results";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get auto-draft results for a specific date (admin only)
 */
export const getAutoDraftResultsByDateAsAdmin = createAsyncThunk(
	"admin/getAutoDraftResultsByDate",
	async (params: AutoDraftResultsByDateParams, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftResultsByDateAsAdmin(params);
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft results for date";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get failed auto-draft attempts (admin only)
 */
export const getAutoDraftFailedAsAdmin = createAsyncThunk(
	"admin/getAutoDraftFailed",
	async (params: AutoDraftFailedParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftFailedAsAdmin(params);
			return {
				attempts: response.data || [],
				pagination: response.pagination,
				failureAnalysis: response.failureAnalysis,
			};
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch failed auto-draft attempts";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get auto-draft retry/pending attempts (admin only)
 */
export const getAutoDraftRetriesAsAdmin = createAsyncThunk(
	"admin/getAutoDraftRetries",
	async (params: AutoDraftRetryParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftRetriesAsAdmin(params);
			return {
				attempts: response.data || [],
				pagination: response.pagination,
				upcomingRetries: response.upcomingRetries || [],
			};
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft retry attempts";
			return rejectWithValue(message);
		}
	},
);

/**
 * Get details of a specific auto-draft attempt (admin only)
 */
export const getAutoDraftAttemptByIdAsAdmin = createAsyncThunk(
	"admin/getAutoDraftAttemptById",
	async (attemptId: string, { rejectWithValue }) => {
		try {
			const response = await fetchAutoDraftAttemptByIdAsAdmin(attemptId);
			return response.data || response;
		} catch (error: any) {
			const message =
				error.response?.data?.message ||
				error.message ||
				"Failed to fetch auto-draft attempt details";
			return rejectWithValue(message);
		}
	},
);
